import React, { useContext, useEffect, useState } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useColorScheme,
  View,
} from "react-native";
import { AntDesign, Feather } from "@expo/vector-icons";
import { useIsFocused } from "@react-navigation/native";
import { useToast } from "react-native-toast-notifications";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { deleteBypassUserById } from "../../../services/usersService";
import UpdateConfirmationScreen from "../../Operations/partials/UpdateConfirmationScreen";
import {
  deleteOrganizationalUserById,
  getOrganizationalUserById,
} from "../../../services/adminService";

interface Props {
  navigation: any;
  route: any;
}

const AdminOrganizationalUsersNavigate: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const toast = useToast();
  const { isMediumDevice, isMobileDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const isFocused = useIsFocused();
  const [title, setTitle] = useState("");
  const [action, setAction] = useState("");
  const [confirmationTextCS, setConfirmationTextCS] = useState("");
  const [successfulTextCS, setSuccessfulTextCS] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [approved, setApproved] = useState(false);
  let { mode, userId } = route.params;
  const [selectedUser, setSelectedUser] = useState(mode || null);
  const colorScheme = useColorScheme();

  useEffect(() => {
    if (mode == undefined || userId == undefined) {
      setTimeout(() => {
        navigation.reset({
          index: 0,
          routes: [
            {
              name: "AdminHome",
              params: { screen: "AdminOrganizationalUsers" },
            },
          ],
        });
      }, 1500);
    } else {
      getOrganizationalUserById(userId)
        .then((data) => setSelectedUser(data))
        .catch((error) => toast.show(error));
    }
  }, [mode, userId]);

  useEffect(() => {
    if (isFocused) {
      switch (mode) {
        case "edit":
          setTitle("View User");
          setApproved(true);
          break;
        case "delete":
          setTitle("Delete User");
          setApproved(false);
          break;
        default:
          break;
      }
      setAction(mode);
      setLoaded(true);
    } else setLoaded(false);
  }, [isFocused]);

  useEffect(() => {
    // Handle title and confirmation text based on action
    switch (action) {
      case "edit":
        setTitle("View User");
        break;
      case "delete":
        setTitle("Delete User");
        setConfirmationTextCS(
          "Are you sure you want to delete this organizational user?"
        );
        break;
      default:
        setTitle("");
        setConfirmationTextCS("");
        break;
    }
  }, [action]);

  useEffect(() => {
    // Directly call deleteOperation when approved and action is delete
    if (approved && action === "delete") {
      // Validate selectedUser before attempting to delete
      if (selectedUser && selectedUser._id) {
        deleteDirectOperation(selectedUser._id);
      } else {
        console.error("No user selected for deletion.");
      }
    }
  }, [approved, action]);

  const approveCS = () => {
    setApproved(true);
  };

  const rejectCS = () => {
    navigation.reset({
      index: 0,
      routes: [
        { name: "AdminHome", params: { screen: "AdminOrganizationalUsers" } },
      ],
    });
  };

  const updateOperation = () => {
    navigation.navigate("UpdateAdminOrganizationUser", { userId: userId });
  };

  const deleteDirectOperation = async (id: string) => {
    await deleteOrganizationalUserById(id)
      .then((result) => {
        toast.show(result?.data.message, {
          type: "success",
        });
        navigation.reset({
          index: 0,
          routes: [
            {
              name: "AdminHome",
              params: { screen: "AdminOrganizationalUsers" },
            },
          ],
        });
      })
      .catch((err) => {
        toast.show(err.message, {
          type: "danger",
        });
      });
  };

  return (
    <>
      {approved ? (
        <ScrollView
          style={{
            height: "100%",
            backgroundColor: colorScheme === "light" ? "#fff" : "#fff",
          }}
        >
          {/* User Details Section */}
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <View style={{ paddingHorizontal: 15, paddingVertical: 5 }}>
                <TouchableOpacity
                  onPress={() =>
                    navigation.navigate("AdminOrganizationalUsers")
                  }
                >
                  <AntDesign name="arrowleft" size={24} color="black" />
                </TouchableOpacity>
              </View>
              <Text style={styles.cardTitle}>User Details</Text>
              {action === "edit" && (
                <TouchableOpacity onPress={updateOperation}>
                  <Feather name="edit" size={20} color="#007BFF" />
                </TouchableOpacity>
              )}
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>First Name:</Text>
              <Text style={styles.value}>{selectedUser.first_name}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Last Name:</Text>
              <Text style={styles.value}>{selectedUser.last_name}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Zone Info:</Text>
              <Text style={styles.value}>{selectedUser.zone_info}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Email:</Text>
              <Text style={styles.value}>{selectedUser.email}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Username:</Text>
              <Text style={styles.value}>{selectedUser.login || ""}</Text>
            </View>
          </View>

          {/* Summary Section */}
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Text style={styles.cardTitle}>Summary</Text>
              {action === "edit" && (
                <TouchableOpacity onPress={updateOperation}>
                  <Feather name="edit" size={20} color="#007BFF" />
                </TouchableOpacity>
              )}
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Team:</Text>
              <Text style={styles.value}>{selectedUser.team}</Text>
            </View>
            {selectedUser?.roles?.map((role: any, index: number) => (
              <View key={index} style={styles.card}>
                <View style={styles.row}>
                  <Text style={styles.label}>Role:</Text>
                  <Text style={styles.value}>{role.role}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Company:</Text>
                  <Text style={styles.value}>
                    {role?.company?.name || "N/A"}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </ScrollView>
      ) : (
        <UpdateConfirmationScreen
          confirmationText={confirmationTextCS}
          successfulText={successfulTextCS}
          approve={approveCS}
          reject={rejectCS}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: 15,
    paddingBottom: 25,
  },
  formHeaders: {
    color: "#B5B5B5",
    fontSize: 20,
    fontWeight: "500",
    marginBottom: 7,
  },
  formSection: {
    paddingVertical: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#000",
  },
  customerRow: {
    flexDirection: "row",
    paddingVertical: 8,
  },
  shippingRow: {
    flexDirection: "row",
    paddingVertical: 5,
  },
  checkbox: {
    paddingLeft: 0,
    marginLeft: -7,
    paddingVertical: 0,
  },
  checkboxLabel: {
    marginTop: 2,
    textAlign: "left",
    justifyContent: "center",
    fontWeight: "500",
  },
  datatableHeader: {
    height: 45,
    backgroundColor: "#D9D9D9",
  },
  productColumn: {
    flex: 2.75,
    paddingVertical: 0,
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  orderQtyColumn: {
    flex: 0.75,
    paddingVertical: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  uom: {
    flex: 1.1,
    justifyContent: "center",
    paddingVertical: 0,
    alignItems: "center",
  },
  inventory: {
    flex: 1.1,
    justifyContent: "center",
    paddingVertical: 0,
    alignItems: "center",
  },
  price: {
    flex: 1.2,
    justifyContent: "flex-end",
    paddingVertical: 0,
    alignItems: "center",
  },
  remarksInput: {
    borderWidth: 1,
    borderColor: "#000",
    height: 60,
    padding: 4,
  },
  datatableHeaderItemText: {
    fontSize: 12,
    textAlign: "center",
    lineHeight: 16,
    color: "#000",
  },
  headerBtnContainer: {
    padding: 5,
    marginHorizontal: 3,
  },
  fileList: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-evenly",
    marginTop: 20,
  },
  filePreviewHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 8,
    paddingVertical: 5,
    backgroundColor: "#f8f8f8",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  fileNameText: {
    fontSize: 12, // Reduced font size
    fontWeight: "400",
    color: "#333",
    flexWrap: "wrap",
    maxWidth: "80%", // Prevents text overflow
  },
  fileLink: {
    fontSize: 12, // Adjusted font size
    color: "#007bff",
    textDecorationLine: "underline",
    textAlign: "center",
    marginTop: 5,
  },
  previewContainer: {
    justifyContent: "center",
    alignItems: "center",
  },
  imagePreview: {
    width: "100%",
    height: 80, // Reduced height for image preview
    backgroundColor: "#f5f5f5",
    borderTopWidth: 1,
    borderTopColor: "#eee",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
  },
  placeholderIconContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#e0e0e0",
    width: "100%",
    height: 80, // Same height for placeholder as the image preview
  },
  pdfLink: {
    fontSize: 12,
    color: "#007aff",
    textDecorationLine: "underline",
    textAlign: "center",
    marginVertical: 3,
  },
  drawerStyle: {
    flexDirection: "row",
    borderBottomWidth: 2,
    borderColor: "grey",
    position: "sticky",
    top: 0,
    width: "100%",
    zIndex: 999,
    backgroundColor: "#fff",
    height: 40,
  },
  container: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    padding: 16,
  },
  header: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: "#007BFF",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  headerText: {
    color: "#FFF",
    fontSize: 20,
    fontWeight: "bold",
    textAlign: "center",
  },
  card: {
    backgroundColor: "#FFF",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#333",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  label: {
    fontSize: 16,
    color: "#555",
    fontWeight: "600",
  },
  value: {
    fontSize: 16,
    color: "#333",
  },
});

export default AdminOrganizationalUsersNavigate;
